import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  Search,
  Add,
  MoreVert,
  Person,
  Mail,
  Block,
  CheckCircle,
  ContentCopy,
  Edit,
  DeleteForever,
  AdminPanelSettings,
} from '@mui/icons-material';
import {
  useUsers,
  useUser,
  useDeleteUser,
  usePermanentDeleteUser,
  usePromoteToGlobalAdmin,
  useDemoteFromGlobalAdmin,
  useResendInvite,
  useUpdateUser,
} from '../../hooks/useUsers';
import { useAuth } from '../../contexts/AuthContext';
import InviteUserDialog from '../../components/InviteUserDialog';
import EditUserDialog from '../../components/EditUserDialog';
import type { ManagedUser } from '../../api/types';
import { SectionTabs } from '../../components/SectionTabs';
import { useManagementTabs, MANAGEMENT_ACCENT_COLOR } from './managementTabs';
import { managementHelp } from '../../components/PageHelp/helpContent';
import { useOrgFilter } from '../../contexts/OrganizationContext';

const statusColors: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  active: 'success',
  pending: 'warning',
  disabled: 'error',
};

const roleColors: Record<string, string> = {
  admin: '#ef4444',
  editor: '#f59e0b',
  viewer: '#6366f1',
};

const UsersPage: React.FC = () => {
  const managementTabs = useManagementTabs();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<ManagedUser | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [permanentDeleteOpen, setPermanentDeleteOpen] = useState(false);
  const [permanentDeleteConfirmText, setPermanentDeleteConfirmText] = useState('');
  const [promoteConfirmOpen, setPromoteConfirmOpen] = useState(false);
  const [demoteConfirmOpen, setDemoteConfirmOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [inviteLinkDialog, setInviteLinkDialog] = useState<{ open: boolean; url: string }>({ open: false, url: '' });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Use global organization filter
  const orgFilter = useOrgFilter();

  const { data, isLoading, error, refetch } = useUsers({
    page: page + 1,
    size: rowsPerPage,
    search: searchQuery || undefined,
    status: statusFilter || undefined,
    organization_id: orgFilter,
  });

  const deleteMutation = useDeleteUser();
  const permanentDeleteMutation = usePermanentDeleteUser();
  const promoteMutation = usePromoteToGlobalAdmin();
  const demoteMutation = useDemoteFromGlobalAdmin();
  const resendMutation = useResendInvite();
  const updateMutation = useUpdateUser();

  // Read the current user's own row to check global_role for gating the
  // hard-delete action. AuthContext's user only exposes Better Auth fields
  // (no global_role), so fetch the full ManagedUser by id.
  const { user: authUser } = useAuth();
  const { data: currentUserDetail } = useUser(authUser?.id || '');
  const isGlobalAdmin = currentUserDetail?.global_role === 'global_admin';

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: ManagedUser) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    setEditDialogOpen(true);
    setAnchorEl(null);
  };

  const handleEditSuccess = () => {
    setSnackbar({ open: true, message: 'User updated successfully', severity: 'success' });
    refetch();
  };

  const handleDeleteConfirm = async () => {
    if (selectedUser) {
      try {
        await deleteMutation.mutateAsync(selectedUser.id);
        setSnackbar({ open: true, message: 'User disabled successfully', severity: 'success' });
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to disable user', severity: 'error' });
      }
    }
    setDeleteConfirmOpen(false);
    setSelectedUser(null);
  };

  const handlePermanentDeleteClick = () => {
    setPermanentDeleteConfirmText('');
    setPermanentDeleteOpen(true);
    setAnchorEl(null);
  };

  const handlePromoteClick = () => {
    setPromoteConfirmOpen(true);
    setAnchorEl(null);
  };

  const handleDemoteClick = () => {
    setDemoteConfirmOpen(true);
    setAnchorEl(null);
  };

  const handlePromoteConfirm = async () => {
    if (!selectedUser) return;
    try {
      const result = await promoteMutation.mutateAsync(selectedUser.id);
      setSnackbar({ open: true, message: result.message || 'User promoted', severity: 'success' });
      setPromoteConfirmOpen(false);
      setSelectedUser(null);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || 'Failed to promote user',
        severity: 'error',
      });
    }
  };

  const handleDemoteConfirm = async () => {
    if (!selectedUser) return;
    try {
      const result = await demoteMutation.mutateAsync(selectedUser.id);
      setSnackbar({ open: true, message: result.message || 'User demoted', severity: 'success' });
      setDemoteConfirmOpen(false);
      setSelectedUser(null);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || 'Failed to demote user',
        severity: 'error',
      });
    }
  };

  const handlePermanentDeleteConfirm = async () => {
    if (!selectedUser) return;
    try {
      await permanentDeleteMutation.mutateAsync(selectedUser.id);
      setSnackbar({ open: true, message: 'User permanently deleted', severity: 'success' });
      setPermanentDeleteOpen(false);
      setPermanentDeleteConfirmText('');
      setSelectedUser(null);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || 'Failed to permanently delete user',
        severity: 'error',
      });
    }
  };

  const handleResendInvite = async () => {
    if (selectedUser) {
      try {
        const result = await resendMutation.mutateAsync(selectedUser.id);
        setInviteLinkDialog({ open: true, url: window.location.origin + result.invite_url });
        setSnackbar({ open: true, message: 'Invite link generated', severity: 'success' });
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to generate invite link', severity: 'error' });
      }
    }
    handleMenuClose();
  };

  const handleToggleStatus = async () => {
    if (selectedUser) {
      const newStatus = selectedUser.status === 'disabled' ? 'active' : 'disabled';
      try {
        await updateMutation.mutateAsync({ id: selectedUser.id, data: { status: newStatus } });
        setSnackbar({ open: true, message: `User ${newStatus === 'active' ? 'enabled' : 'disabled'}`, severity: 'success' });
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to update user status', severity: 'error' });
      }
    }
    handleMenuClose();
  };

  const handleInviteSuccess = (inviteUrl: string) => {
    setInviteLinkDialog({ open: true, url: window.location.origin + inviteUrl });
    setSnackbar({ open: true, message: 'User invited successfully', severity: 'success' });
    setInviteDialogOpen(false);
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLinkDialog.url);
    setSnackbar({ open: true, message: 'Invite link copied to clipboard', severity: 'success' });
  };

  const users = data?.data || [];
  const totalCount = data?.total || 0;

  return (
    <Box sx={{ p: 3 }}>
      <SectionTabs
        title="Management"
        tabs={managementTabs}
        accentColor={MANAGEMENT_ACCENT_COLOR}
        help={managementHelp.users}
        actionButton={{
          label: 'Invite User',
          icon: <Add />,
          onClick: () => setInviteDialogOpen(true),
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load users. Please try again.
        </Alert>
      )}

      <Paper sx={{ bgcolor: '#1e1e1e', borderRadius: 2 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #333', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by name or email..."
            size="small"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>

        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#6366f1' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Organizations</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Person sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography color="text.secondary">
                          No users found. Invite your first user to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person sx={{ color: '#6366f1' }} />
                            <Box>
                              <Typography fontWeight={500}>{user.name || '(No name)'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {user.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.global_role === 'global_admin' ? 'Global Admin' : user.role}
                            size="small"
                            sx={{
                              bgcolor: user.global_role === 'global_admin' ? '#dc2626' : roleColors[user.role] || '#666',
                              color: 'white',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                            size="small"
                            color={statusColors[user.status]}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {user.organizations?.slice(0, 2).map((org) => (
                              <Chip key={org.id} label={org.name} size="small" variant="outlined" />
                            ))}
                            {(user.organizations?.length || 0) > 2 && (
                              <Chip label={`+${(user.organizations?.length || 0) - 2}`} size="small" variant="outlined" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {user.last_login_at
                              ? new Date(user.last_login_at).toLocaleDateString()
                              : user.status === 'pending' ? 'Never' : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, user)}>
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit User</ListItemText>
        </MenuItem>
        {selectedUser?.status === 'pending' && (
          <MenuItem onClick={handleResendInvite}>
            <ListItemIcon>
              <Mail fontSize="small" />
            </ListItemIcon>
            <ListItemText>Resend Invite</ListItemText>
          </MenuItem>
        )}
        {selectedUser?.status !== 'pending' && (
          <MenuItem onClick={handleToggleStatus}>
            <ListItemIcon>
              {selectedUser?.status === 'disabled' ? <CheckCircle fontSize="small" /> : <Block fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{selectedUser?.status === 'disabled' ? 'Enable User' : 'Disable User'}</ListItemText>
          </MenuItem>
        )}
        {isGlobalAdmin && selectedUser?.status === 'active' && selectedUser?.global_role !== 'global_admin' && (
          <MenuItem onClick={handlePromoteClick}>
            <ListItemIcon>
              <AdminPanelSettings fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Promote to Global Admin</ListItemText>
          </MenuItem>
        )}
        {isGlobalAdmin && selectedUser?.global_role === 'global_admin' && selectedUser?.id !== authUser?.id && (
          <MenuItem onClick={handleDemoteClick}>
            <ListItemIcon>
              <AdminPanelSettings fontSize="small" sx={{ color: 'text.secondary' }} />
            </ListItemIcon>
            <ListItemText>Demote from Global Admin</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Block fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>Disable</ListItemText>
        </MenuItem>
        {selectedUser?.status === 'disabled' && isGlobalAdmin && (
          <MenuItem onClick={handlePermanentDeleteClick} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteForever fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText>Delete Permanently</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onSuccess={handleInviteSuccess}
      />

      {/* Edit User Dialog */}
      <EditUserDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSuccess={handleEditSuccess}
      />

      {/* Invite Link Dialog */}
      <Dialog open={inviteLinkDialog.open} onClose={() => setInviteLinkDialog({ open: false, url: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Link</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Copy this link and send it to the user. The link will expire in 7 days.
          </Typography>
          <TextField
            fullWidth
            value={inviteLinkDialog.url}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copy to clipboard">
                    <IconButton onClick={copyInviteLink}>
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteLinkDialog({ open: false, url: '' })}>Close</Button>
          <Button variant="contained" onClick={copyInviteLink} startIcon={<ContentCopy />}>
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disable Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Disable User?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to disable <strong>{selectedUser?.name || selectedUser?.email}</strong>?
            They will no longer be able to log in.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog
        open={permanentDeleteOpen}
        onClose={() => {
          if (!permanentDeleteMutation.isPending) {
            setPermanentDeleteOpen(false);
            setPermanentDeleteConfirmText('');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>Permanently Delete User?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action is irreversible. The user row, their sessions, role assignments,
            invites, and organization memberships will be removed. Content, schedules,
            and playlists they created will be preserved but no longer attributed.
          </Alert>
          <Typography sx={{ mb: 2 }}>
            Type <strong>{selectedUser?.email}</strong> to confirm.
          </Typography>
          <TextField
            fullWidth
            autoFocus
            value={permanentDeleteConfirmText}
            onChange={(e) => setPermanentDeleteConfirmText(e.target.value)}
            placeholder={selectedUser?.email || ''}
            disabled={permanentDeleteMutation.isPending}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPermanentDeleteOpen(false);
              setPermanentDeleteConfirmText('');
            }}
            disabled={permanentDeleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePermanentDeleteConfirm}
            color="error"
            variant="contained"
            disabled={
              permanentDeleteMutation.isPending ||
              permanentDeleteConfirmText !== selectedUser?.email
            }
            startIcon={<DeleteForever />}
          >
            {permanentDeleteMutation.isPending ? <CircularProgress size={20} /> : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Promote to Global Admin Dialog */}
      <Dialog open={promoteConfirmOpen} onClose={() => setPromoteConfirmOpen(false)}>
        <DialogTitle>Promote to Global Admin?</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Global Admins manage the entire platform — all organizations, sites, and
            zones. They are JustPlay-internal and must not belong to any customer
            organization. Any existing organization memberships will block this
            action and need to be removed first.
          </Alert>
          <Typography>
            Promote <strong>{selectedUser?.name || selectedUser?.email}</strong> to
            Global Admin?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteConfirmOpen(false)} disabled={promoteMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handlePromoteConfirm}
            color="primary"
            variant="contained"
            startIcon={<AdminPanelSettings />}
            disabled={promoteMutation.isPending}
          >
            {promoteMutation.isPending ? <CircularProgress size={20} /> : 'Promote'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Demote from Global Admin Dialog */}
      <Dialog open={demoteConfirmOpen} onClose={() => setDemoteConfirmOpen(false)}>
        <DialogTitle>Demote from Global Admin?</DialogTitle>
        <DialogContent>
          <Typography>
            Remove Global Admin privileges from <strong>{selectedUser?.name || selectedUser?.email}</strong>?
            They will retain access only to any customer organizations they later
            join. This cannot be undone without another Global Admin re-promoting them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDemoteConfirmOpen(false)} disabled={demoteMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDemoteConfirm}
            color="warning"
            variant="contained"
            disabled={demoteMutation.isPending}
          >
            {demoteMutation.isPending ? <CircularProgress size={20} /> : 'Demote'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersPage;
